import { useQuery } from '@tanstack/react-query';
import { useCallback } from 'react';
import { apiFetch } from '../lib/api';

interface CurrencySettings {
  base: string;
  display: string;
  symbol: string;
  locale: string;
}

interface ExchangeRates {
  rates: Record<string, number>;
  lastUpdated: string | null;
}

interface TaxConfig {
  name: string;
  rate: number;
  isActive: boolean;
}

interface CurrencyData {
  currency: CurrencySettings;
  exchangeRates: ExchangeRates;
  rounding: number;
  taxes: TaxConfig[];
}

export function useCurrency() {
  const { data, isLoading } = useQuery({
    queryKey: ['currency'],
    queryFn: async () => {
      const res = await apiFetch<CurrencyData>('/currency');
      return res.data as CurrencyData;
    },
    staleTime: 10 * 60 * 1000, // 10 minutes
  });

  const currency = data?.currency ?? { base: 'USD', display: 'USD', symbol: '$', locale: 'en-US' };
  const rates = data?.exchangeRates?.rates ?? {};
  const rounding = data?.rounding ?? 1;
  const taxes = (data?.taxes ?? []).filter((t: TaxConfig) => t.isActive);
  const totalTaxRate = taxes.reduce((sum: number, t: TaxConfig) => sum + (t.rate / 100), 0);

  // Format price without tax (for raw values, cost prices)
  const formatPriceNoTax = useCallback(
    (cents: number | undefined | null): string => {
      const value = cents ?? 0;
      if (!data) return `$${(value / 100).toFixed(2)} USD`;

      let amount = value;
      if (currency.display !== currency.base && rates[currency.display] && rates[currency.base]) {
        const inBase = value / (rates[currency.base] || 1);
        amount = Math.round(inBase * (rates[currency.display] || 1));
      }

      try {
        const formatted = new Intl.NumberFormat(currency.locale, {
          style: 'currency',
          currency: currency.display,
          minimumFractionDigits: 2,
        }).format(amount / 100);
        return `${formatted} ${currency.display}`;
      } catch {
        return `${currency.symbol}${(amount / 100).toFixed(2)} ${currency.display}`;
      }
    },
    [data, currency, rates]
  );

  // Format price WITH tax + rounding (default for customer-facing views)
  const formatPrice = useCallback(
    (cents: number | undefined | null): string => {
      const value = cents ?? 0;
      const withTax = value + Math.round(value * totalTaxRate);
      let amount = withTax;
      // Convert currency
      if (currency.display !== currency.base && rates[currency.display] && rates[currency.base]) {
        const inBase = withTax / (rates[currency.base] || 1);
        amount = Math.round(inBase * (rates[currency.display] || 1));
      }
      // Round
      if (rounding > 1) {
        const roundingCents = rounding * 100;
        amount = Math.ceil(amount / roundingCents) * roundingCents;
      }
      if (!data) return `$${(amount / 100).toFixed(2)} USD`;
      try {
        const formatted = new Intl.NumberFormat(currency.locale, {
          style: 'currency',
          currency: currency.display,
          minimumFractionDigits: 2,
        }).format(amount / 100);
        return `${formatted} ${currency.display}`;
      } catch {
        return `${currency.symbol}${(amount / 100).toFixed(2)} ${currency.display}`;
      }
    },
    [data, currency, rates, rounding, totalTaxRate]
  );

  // Format order price (already includes tax, uses order's locked currency)
  const formatOrderPrice = useCallback(
    (cents: number, orderCurrency: string): string => {
      try {
        const formatted = new Intl.NumberFormat(currency.locale || 'en-US', {
          style: 'currency',
          currency: orderCurrency,
          minimumFractionDigits: 2,
        }).format(cents / 100);
        return `${formatted} ${orderCurrency}`;
      } catch {
        return `$${(cents / 100).toFixed(2)} ${orderCurrency}`;
      }
    },
    [currency.locale]
  );

  // Calculate tax on a cents amount
  const calculateTax = useCallback(
    (cents: number): number => Math.round(cents * totalTaxRate),
    [totalTaxRate]
  );

  // Get the final price in cents (with tax + rounding) for computing totals
  const priceWithTax = useCallback(
    (cents: number): number => {
      let amount = cents + Math.round(cents * totalTaxRate);
      // Convert currency if needed
      if (currency.display !== currency.base && rates[currency.display] && rates[currency.base]) {
        const inBase = amount / (rates[currency.base] || 1);
        amount = Math.round(inBase * (rates[currency.display] || 1));
      }
      if (rounding > 1) {
        const roundingCents = rounding * 100;
        amount = Math.ceil(amount / roundingCents) * roundingCents;
      }
      return amount;
    },
    [totalTaxRate, rounding, currency, rates]
  );

  return {
    formatPrice,        // With tax + rounding (for catalog/package prices)
    formatPriceNoTax,   // Without tax (for subtotals, cost breakdowns)
    formatOrderPrice,   // For existing orders (already have tax calculated)
    calculateTax,
    priceWithTax,
    taxes,
    totalTaxRate,
    currency: currency.display,
    baseCurrency: currency.base,
    symbol: currency.symbol,
    isLoading,
  };
}
